/* eslint-disable @stripe-internal/embedded/no-restricted-globals */
import React from 'react';
import type {FetchApiError} from '@stripe-internal/connect-embedded-lib';
import type {OpenapiPostV1EmbeddedAuthenticationLinksInputLinkTypeEnum} from '@stripe-internal/data-v1/schema/types';
import {windowOpen} from '@stripe-internal/safe-links';
import type {MetricTags} from '@sail/observability';
import {useAnalytics, useMetrics, useReports} from '@sail/observability';
import {getColor} from '@stripe-internal/embedded-theming';
import {getCurrentScriptUrlContext} from '../utils/getCurrentScriptUrlContext';
import type {IFrameEventMap, IFrameMessenger} from './FrameMessenger';
import {getStripeConnectHost} from './buildStripeClient';
import type {AuthenticationState} from '../data-layer-frame/authentication/types';
import {uuid} from '../utils/uuid';

import {
  SubmerchantAuthError,
  SubmerchantAuthExpectedError,
  SubmerchantAuthSessionExpiredError,
  TimeoutError,
} from './SubmerchantAuthenticationError';
import {DeferredPromise} from '../utils/DeferredPromise';
import {useAssertions} from './assert';
import {getDevLogger} from '../utils/getLogger';
import {useConnectJsOptionsContext} from './ConnectJsOptionsContext/useConnectJsOptionsContext';
import type {HostApp} from '../connect/ConnectJSInterface/InitAndUpdateOptionsTypes';
import {useAuthContext} from './AuthContext/AuthContext';
import useEmbeddedAuthenticationLinkMutation from './useEmbeddedAuthenticationLinkMutation';
import useRetrieveAuthenticatedUserLazyQuery from './queries/useRetrieveAuthenticatedUserLazyQuery';
import type {AuthenticatedUser} from '../data-layer-frame/types';
import {withErrorReportingAndAnalytics} from './ErrorReportingAndAnalytics';
import {isHostedOnboardingHostApp} from '../utils/getHostApp';
import {useFeature} from './FeaturesContext';
import {useEmitEvent} from './EventEmitterContext';

type SubmerchantAuthenticationContextProps = {
  locale: string;
  frameMessenger: IFrameMessenger;
  submerchantAuthOverride?: SubmerchantAuthentication;
  status: SubmerchantAuthenticationStatus;
  setStatus: (status: SubmerchantAuthenticationStatus) => void;
  apiKeyOverride: string | undefined;
  hostApp: HostApp | undefined;
};

type ProfileEditType = 'edit_email' | 'edit_phone';

export const SubmerchantAuthenticationContext =
  React.createContext<SubmerchantAuthenticationContextProps | null>(null);
SubmerchantAuthenticationContext.displayName =
  'SubmerchantAuthenticationContext';

// idle, authenticated and pending all refer to authentication state
// for onboarding state, use isOnboarding
export type SubmerchantAuthentication = {
  isAuthenticated: boolean; // not only has authentication occurred, but we have also retrieved the authenticated user
  isInitialized: boolean; // has the authentication state been initialized
  isPending: boolean; // authentication is in progress
  isErrored: boolean; // an error occurred during authentication
  error: Error | null;
  isOnboarding: boolean;
  isEditingProfile: boolean;
  authenticate: () => Promise<boolean>;
  onboard: (
    linkType: OnboardingLinkType,
    hash?: string | undefined,
  ) => Promise<boolean>;
  editProfile: (action: ProfileEditType) => Promise<void>;
  logout: (clearUserSessionOnly?: boolean) => Promise<void>;
  cancel: () => void;
  isAuthenticationRequired: boolean; // authentication is supported and has not occurred, yet
  isAccountManagementSupported: boolean; // refers to account management and connect onboarding, supported only for the primary_user
  isApiKeyOverride: boolean; // refers to whether an API key override is being used
};

type SubmerchantAuthenticationStatus =
  | {status: 'idle'}
  | {status: 'authenticated'}
  | {status: 'pending'}
  | {status: 'errored'; error: Error};

export const calculatePopupParams = (width: number, height: number): string => {
  const top = window.screenTop + window.outerHeight / 2 - height / 2;
  const left = window.screenLeft + window.outerWidth / 2 - width / 2;
  return `popup=true,width=${width},height=${height},left=${left},top=${top}`;
};

const POPUP_NAME = 'submerchant-auth-login-window';

type OnboardingLinkType =
  | 'connect_account_onboarding'
  | 'connect_account_update';

/* eslint-disable camelcase */
type PopupPromiseMap = {
  embedded_auth: AuthenticationState | 'closed';
  embedded_user_update: 'complete' | 'closed';
  connect_account_onboarding: 'complete' | 'closed';
  connect_account_update: 'complete' | 'closed';
};
/* eslint-enable camelcase */

interface IAuthPopup<Key extends keyof PopupPromiseMap> {
  window?: Window;
  requestType: Key;
  popupResultPromise: Promise<PopupPromiseMap[Key]>;
}

interface CreatePopupArgs<Key extends keyof PopupPromiseMap> {
  requestType: Key;
  completionPromise: Promise<PopupPromiseMap[Key]>;
  width?: number;
  height?: number;
  hash?: string;
  searchParams?: Record<string, string>;
}

interface WaitForMessageArgs<Event extends keyof IFrameEventMap> {
  event: Event;
  timeout: number;
  start: number;
  tags?: MetricTags<'connect_auth_embedded_popup_event'>;
}

// only one popup window is shown for each connectjs instance,
// even when multiple instances of useSubmerchantAuthentication exist
let globalAuthPopup: IAuthPopup<keyof PopupPromiseMap> | undefined;
function checkForExistingPopup<Key extends keyof PopupPromiseMap>(
  requestType: Key,
): IAuthPopup<Key> | undefined {
  // If there is an existing window, we always want to focus on it.
  globalAuthPopup?.window?.focus();

  if (globalAuthPopup != null) {
    if (globalAuthPopup.requestType !== requestType) {
      throw new SubmerchantAuthExpectedError(
        'Window was already open with different content.',
      );
    }

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return globalAuthPopup as IAuthPopup<Key>;
  }

  return undefined;
}

type AllowedMobileAuthenticationFlow = 'embedded_mobile';

export const useSubmerchantAuthentication = (
  options: {
    mobileAuthenticationFlow?: AllowedMobileAuthenticationFlow;
  } = {},
): SubmerchantAuthentication => {
  const [isInitialized, setIsInitialized] = React.useState<boolean>(false);
  const [isOnboarding, setIsOnboarding] = React.useState<boolean>(false);
  const [authenticatedUser, setAuthenticatedUser] =
    React.useState<AuthenticatedUser | null>(null);
  const [isEditingProfile, setIsEditingProfile] =
    React.useState<boolean>(false);
  const metrics = useMetrics();
  const {
    values: {appearance, metaOptions},
  } = useConnectJsOptionsContext();
  const {mobileAuthenticationFlow} = options;
  const mobileSdk = metaOptions?.mobileSdk;
  const analytics = useAnalytics();
  const reports = useReports();
  const emitEvent = useEmitEvent();

  const {reportError, softAssertFailed} = useAssertions();
  const {authInfo} = useAuthContext();

  const context = React.useContext(SubmerchantAuthenticationContext);
  if (!context) {
    throw new Error('SubmerchantAuthenticationContext should be defined');
  }

  const {
    locale,
    frameMessenger,
    submerchantAuthOverride,
    status,
    setStatus,
    apiKeyOverride,
    hostApp,
  } = context;
  const stripeUserAuthenticationDisabled = useFeature(
    'disable_stripe_user_authentication',
  );

  const isAuthenticationEnabled: boolean = React.useMemo(() => {
    // If mobileSdk is true, the authentication flow must be explicitly set
    if (mobileSdk && !mobileAuthenticationFlow) {
      return false;
    }

    // If authInfo is undefined, we should assume that authentication is supported. Note this may cause false positives for a short period of time
    // while loading since we don't know what the value of stripeUserAuthenticationDisabled is during that time.
    if (!authInfo) {
      return true;
    }

    // Note that authentication is not supported only in the case where stripeUserAuthenticationDisabled is true. Today, this can
    // only be set to true for custom or UA3 accounts (accounts where stripe owns onboarding/requirements collection)
    if (stripeUserAuthenticationDisabled) {
      return false;
    }

    return true;
  }, [
    authInfo,
    mobileAuthenticationFlow,
    mobileSdk,
    stripeUserAuthenticationDisabled,
  ]);

  const [retrieveAuthenticatedUserLazyQuery] =
    useRetrieveAuthenticatedUserLazyQuery();
  const [createEmbeddedAuthenticationLink] =
    useEmbeddedAuthenticationLinkMutation({
      expectedErrors: ['rate_limit'],
    });

  // presence of authPopup indicates the global popup is connected to this particular useSubmerchantAuthentication instance
  // each instance has its own setInterval handle and detect the closing of popup individually
  const authPopup = React.useRef<{
    timer: number; // intervel id
  } | null>(null);

  // clean up after authPopup is deemed as completed, closed preemptively, or otherwise errored
  const authPopupCleanup = React.useCallback(() => {
    clearInterval(authPopup.current?.timer);
    authPopup.current = null;

    globalAuthPopup?.window?.close();
    globalAuthPopup = undefined;
  }, []);

  React.useEffect(() => {
    // set up listening for auth state changes from the iframe
    const listener = (eventData: AuthenticationState) => {
      if (eventData.status === 'user_authenticated') {
        if (eventData.user) {
          setAuthenticatedUser(eventData.user);
        } else {
          retrieveAuthenticatedUserLazyQuery();
        }
      } else if (eventData.status === 'error') {
        setStatus({
          status: 'errored',
          error: eventData.error,
        });
      } else if (eventData.userSessionExpired) {
        setStatus({
          status: 'errored',
          error: new SubmerchantAuthSessionExpiredError(),
        });
        setAuthenticatedUser(null);
      } else {
        setStatus({status: 'idle'});
        setAuthenticatedUser(null);
      }
    };

    frameMessenger.addListener('authStateChanged', listener);
    return () => {
      frameMessenger.removeListener('authStateChanged', listener);
    };
  }, [frameMessenger, setStatus, retrieveAuthenticatedUserLazyQuery]);

  React.useEffect(() => {
    if (authenticatedUser) {
      setStatus({status: 'authenticated'});
    }
  }, [authenticatedUser, setStatus]);

  React.useEffect(() => {
    const run = async () => {
      const result = await frameMessenger.getAuthState();
      setIsInitialized(true);
      if (result.status === 'user_authenticated') {
        if (result.user) {
          setAuthenticatedUser(result.user);
        } else {
          // we shouldn't get here - on load, if we're user_authenticated we should have a user
          retrieveAuthenticatedUserLazyQuery();
        }
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const abortController = React.useRef(new AbortController());

  const waitForMessage = React.useCallback(
    <Event extends keyof IFrameEventMap>({
      event,
      timeout,
      start = performance.now(),
      tags,
    }: WaitForMessageArgs<Event>) => {
      const promise = new Promise<IFrameEventMap[Event]>((resolve, reject) => {
        const timeoutRef = window.setTimeout(() => {
          reject(new TimeoutError(`Timeout waiting for "${event}" event`));
        }, timeout);

        frameMessenger.addListener(event, (result) => {
          clearTimeout(timeoutRef);
          resolve(result);
        });
      });

      return promise.then(
        (result) => {
          metrics.gauge(
            'connect_auth_embedded_popup_event',
            {duration: performance.now() - start},
            {
              ...tags,
              popup_event: event,
              result: 'success',
            },
          );
          return Promise.resolve(result);
        },
        (reason) => {
          metrics.gauge(
            'connect_auth_embedded_popup_event',
            {duration: performance.now() - start},
            {
              ...tags,
              popup_event: event,
              result: 'error',
            },
          );
          return Promise.reject(reason);
        },
      );
    },
    [frameMessenger, metrics],
  );

  /**
   * Returns the query params that we need to pass to every popup URL we load.
   *
   * This includes parameters like the "origin" to postMessage back to,
   * the controller ID for the iframe, the language the user has selected,
   * and any theming attributes the platform may have provided.
   *
   * Even though this call is asynchronous, it *must* be completed quickly.
   * We use this method to load the initial popup, which means that the
   * browser may block the process if the method takes too long to load.
   * We should not make this call based on anything that we don't already
   * have loaded on either the iframe (which we can only access async) or
   * the client-side code.
   *
   * TODO: Can we actually get the frame name synchronously?
   */
  const getDefaultSearchParams = React.useCallback(async () => {
    const params: Record<string, string> = {
      target_origin: getCurrentScriptUrlContext().origin,
      controller_id: await frameMessenger.getFrameName(),
      locale,
    };

    const primaryColor = appearance?.variables?.colorPrimary;
    if (primaryColor) {
      // Note: the auth window expects a hex value, however embedded supports a variety of formats, like hex, RGB and HSL. So we convert this to HEX
      const colorRgb = getColor(primaryColor);
      const hexColor = colorRgb.rgb().formatHex();

      params.primary_color = hexColor.replace('#', '');
    }

    return params;
  }, [frameMessenger, locale, appearance?.variables?.colorPrimary]);

  /**
   * This method fetches the static page we load with a spinner while we wait
   * for the "real URL" to be created.
   *
   * Even though this method is asynchronous, it *must* be quick. Otherwise
   * the browser will block the popup from loading.
   */
  const getLoadingStateUrl = React.useCallback(
    async (
      linkType: OpenapiPostV1EmbeddedAuthenticationLinksInputLinkTypeEnum,
    ) => {
      const env = getCurrentScriptUrlContext().serviceEnvironment;
      const url = new URL(
        `https://${getStripeConnectHost(env)}/embedded/loading`,
      );
      const params = await getDefaultSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        url.searchParams.set(key, value);
      });
      url.searchParams.set('link_type', linkType);
      return url;
    },
    [getDefaultSearchParams],
  );

  const createEmbeddedLinkWrapper = React.useCallback(
    async (
      linkType: OpenapiPostV1EmbeddedAuthenticationLinksInputLinkTypeEnum,
      searchParams: Record<string, string> = {},
      hash = '',
    ) => {
      const defaultSearchParams = await getDefaultSearchParams();
      const embeddedAuthlinkInput = {
        link_type: linkType,
        include: [],
        target_origin: defaultSearchParams.target_origin,
        controller_id: defaultSearchParams.controller_id,
      };

      const {data} = await createEmbeddedAuthenticationLink({
        variables: {input: embeddedAuthlinkInput},
      });

      const link = data?.v1EmbeddedAuthenticationLinks?.url;
      if (link == null) {
        throw new SubmerchantAuthError(
          `Could not generate link for type ${linkType}`,
        );
      }

      const url = new URL(link);
      url.hash = hash;

      const params = {
        ...defaultSearchParams,
        ...searchParams,
      };

      Object.entries(params).forEach(([key, value]) => {
        url.searchParams.set(key, value);
      });

      return url;
    },
    [getDefaultSearchParams, createEmbeddedAuthenticationLink],
  );

  const openPopup = React.useCallback(
    (url: URL, width: number, height: number) => {
      return new Promise<Window>((resolve, reject) => {
        const params = calculatePopupParams(width, height);
        // Needs to use windowOpen to ensure window.opener is available, do not migrate to safeWindowOpen
        const popup = windowOpen(url.toString(), POPUP_NAME, params);

        if (popup == null) {
          reject(new SubmerchantAuthError('Popup is blocked by browser.'));
        } else {
          resolve(popup);
        }
      });
    },
    [],
  );

  const openLoadingPopup = React.useCallback(
    async (
      linkType: OpenapiPostV1EmbeddedAuthenticationLinksInputLinkTypeEnum,
      width: number,
      height: number,
    ) => {
      const loadingStateUrl = await getLoadingStateUrl(linkType);
      return openPopup(loadingStateUrl, width, height);
    },
    [getLoadingStateUrl, openPopup],
  );

  const handleClosedPopup = React.useCallback(
    <Key extends keyof PopupPromiseMap>(result: PopupPromiseMap[Key]) => {
      if (result === 'closed') {
        getDevLogger().warn('Popup closed prematurely.');
        setStatus({
          status: 'errored',
          error: new SubmerchantAuthExpectedError('Popup closed prematurely.'),
        });
      }

      return result;
    },
    [setStatus],
  );

  const createPopup = React.useCallback(
    async <Key extends keyof PopupPromiseMap>({
      requestType,
      completionPromise,
      width = 720,
      height = 800,
      searchParams = {},
      hash = '',
    }: CreatePopupArgs<Key>) => {
      const start = performance.now();

      const metricsTags: MetricTags<'connect_auth_embedded_popup_init'> = {
        popup_flow: requestType,
      };

      // Create a deferred promise that we can use as a proxy reference. This promise
      // is stored globally so that we can return the same result to multiple callers.
      // (e.g. if "authenticate()" is called twice, the same promise result is returned)
      const deferred = new DeferredPromise<PopupPromiseMap[Key]>();

      // Because this promise is a proxy, it's possibly nothing will listen to it and
      // catch it. That makes Chrome unhappy because there could be an unhandled promise
      // rejection. Adding this "catch" makes Chrome and our tests happy again.
      // Note that "await deferred.promise" and "deferred.promise.catch" will still
      // work to capture errors if anyone *wants* to capture them, though.
      deferred.promise.catch(() => {});

      // Establish our "global popup" object with its initial values.
      // We want to do this early, so that other calls can short-circuit if we're
      // already opening a popup.
      globalAuthPopup = {
        requestType,
        popupResultPromise: deferred.promise,
      };

      const assignWindow = (newWindow: Window) => {
        // Because the promise is asynchronous, it's possible (though unlikely)
        // that the globalAuthPopup object would become unassigned while executing.
        // If that *does* happen, then we can't really proceed anymore, so close
        // our new popup window and throw an exception.
        if (globalAuthPopup == null) {
          newWindow.close();
          throw new SubmerchantAuthError(
            'Could not assign window, globalAuthPopup was unassigned.',
          );
        }

        globalAuthPopup.window = newWindow;
      };

      // This triggers when we recieve event "popupDoRefresh" which occurs when an popup
      // account link is reloaded. This will request a fresh account link and open
      // in the popup.
      const refreshListener = async () => {
        const link = await createEmbeddedLinkWrapper(
          requestType,
          searchParams,
          hash,
        );
        openPopup(link, width, height);
      };

      // Opening the popup has a lot of steps to it, and many of them are asynchronous.
      // We wrap the result inside a "mega-promise" sort of thing so that it's a bit
      // easier to capture.
      const promise = new Promise<PopupPromiseMap[Key]>(
        // eslint-disable-next-line no-async-promise-executor
        async (resolve, reject) => {
          try {
            metrics.increment('connect_auth_embedded_popup_init', metricsTags);

            frameMessenger.addListener('popupDoRefresh', refreshListener);

            // Go ahead and start creating the embedded link early on in the process,
            // in case the server response is slow. We won't use the response
            // until later, though.
            const embeddedLinkPromise = createEmbeddedLinkWrapper(
              requestType,
              searchParams,
              hash,
            );

            // This "catch" helps browsers and node know that we've done *something*
            // with the response. But we still want to actually handle errors below,
            // so the "catch" doesn't get assigned to any real results.
            // (This seems to be a quirk of how JavaScript heuristically detects
            // unhandled promise rejections.)
            embeddedLinkPromise.catch(() => {});

            // This promise will be resolved after either:
            // - We receive a "popupReady" event from the popup (which the spinner page has loaded).
            // - 5 seconds, when we mark the "ready" event as a timeout.
            const readyPromise = waitForMessage({
              event: 'popupReady',
              timeout: 5000,
              start,
              tags: metricsTags,
            }).catch((reason) => {
              // Ignore any rejections for the purpose of the actual result.
              // We only want to capture any failures so we can log them to sentry.
              const message = `Failed to receive "popup ready" event. ${reason}`;
              softAssertFailed(message, 'account_ux_infra', metricsTags);
            });

            // Open the popup to a "loading spinner" page while we wait for
            // the server-side call for the embedded link to complete.
            // Even though we use await here, this call *must* return quickly,
            // otherwise the browser will block the popup from opening.
            let newWindow = await openLoadingPopup(
              requestType,
              width,
              height,
            ).catch((reason) => {
              // If the popup was blocked, we capture some metrics.
              metrics.increment(
                'connect_auth_embedded_popup_blocked',
                metricsTags,
              );
              return Promise.reject(reason);
            });
            assignWindow(newWindow);

            // Check to see if the user closed the window. If they did, we need to
            // reject the open request because it did not complete naturally.
            authPopup.current = {
              timer: window.setInterval(() => {
                if (newWindow.closed) {
                  resolve('closed');
                }
              }, 1000),
            };

            // Here we wait to proceed until we've received the "ready" event from the popup.
            await readyPromise;

            const link = await embeddedLinkPromise;

            // It's possible the popup may have been closed in the time it took
            // to get here. Don't try to re-open the popup if that's the case.
            if (!newWindow.closed) {
              // Once we know the spinner has finished loading, we can start loading the
              // real form that was created. We fire off another event here that will
              // record how long it took for the popup to say that it finished loading
              // that page (and timeout after 10 seconds).
              //
              // We don't "await" this one anywhere because, while we want the metrics,
              // we don't want to stop the user from potentially proceeding, whatever the
              // result is.
              waitForMessage({
                event: 'popupLoaded',
                timeout: 10_000,
                start,
                tags: metricsTags,
              }).catch((reason) => {
                if (newWindow.closed) {
                  return;
                }

                // Ignore any rejections for the purpose of the actual result.
                // We only want to capture any failures so we can log them to sentry.
                const message = `Failed to receive "popup loaded" event. ${reason}`;
                softAssertFailed(message, 'account_ux_infra', metricsTags);
              });

              newWindow = await openPopup(link, width, height);
              assignWindow(newWindow);
            }

            // Finally, we return whenever the iframe tells us that the requested
            // action was completed.
            // eslint-disable-next-line no-promise-executor-return
            return completionPromise.then(resolve, reject);
          } catch (e) {
            reject(e);
          }
        },
      );

      // When the promise resolves (or rejects), proxy that response over to the
      // deferred copy of the promise.
      return promise
        .then(
          (result) => {
            metrics.gauge(
              'connect_auth_embedded_popup_complete',
              {duration: performance.now() - start},
              {
                ...metricsTags,
                result: result === 'closed' ? 'error' : 'success',
              },
            );
            deferred.resolve(result);
            return Promise.resolve(result);
          },
          (reason) => {
            const error =
              reason instanceof Error ? reason : Error(String(reason));

            metrics.gauge(
              'connect_auth_embedded_popup_complete',
              {duration: performance.now() - start},
              {...metricsTags, result: 'error'},
            );
            reportError(error, {
              owner: 'account_ux_infra',
              type: 'error',
              ...metricsTags,
            });

            setStatus({status: 'errored', error});
            deferred.reject(reason);
            return Promise.reject(reason);
          },
        )
        .then(handleClosedPopup)
        .finally(() => {
          authPopupCleanup();
          frameMessenger.removeListener('popupDoRefresh', refreshListener);
        });
    },
    [
      authPopupCleanup,
      createEmbeddedLinkWrapper,
      metrics,
      openLoadingPopup,
      openPopup,
      reportError,
      setStatus,
      softAssertFailed,
      waitForMessage,
      handleClosedPopup,
      frameMessenger,
    ],
  );

  const result: SubmerchantAuthentication = React.useMemo(() => {
    const cancel = () => {
      abortController.current.abort();
      abortController.current = new AbortController();
    };

    const logout = async (clearUserSessionOnly?: boolean): Promise<void> => {
      if (status.status === 'pending') {
        cancel();
      }
      try {
        await withErrorReportingAndAnalytics(
          {
            error: reports.error,
            warning: reports.warning,
          },
          metaOptions,
          analytics,
          'submerchant_surfaces_logout',
          frameMessenger.logout,
          clearUserSessionOnly,
          undefined,
          undefined,
          undefined,
          ['invalid_session', 'reauth_required'],
        );
      } catch (error) {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        const status = (error as FetchApiError).error.httpStatus;
        if (status !== 401 && status !== 404) {
          setStatus({
            status: 'errored',
            error: Error('Logout unsuccessful.'),
          });
          return Promise.reject(
            new Error('Logout unsuccessful, please try again.'),
          );
        }
      }
    };

    const authenticate = async (): Promise<boolean> => {
      if (mobileSdk) {
        if (!mobileAuthenticationFlow) {
          return Promise.reject(
            new Error(
              'Authentication in this component is not supported in the mobile SDK.',
            ),
          );
        } else if (!isAuthenticationEnabled) {
          return Promise.reject(new Error('Authentication is not supported'));
        }

        setStatus({status: 'pending'});

        const link = await createEmbeddedLinkWrapper(mobileAuthenticationFlow, {
          is_mobile_sdk: 'true',
        });

        emitEvent('openauthenticatedwebview', {
          id: uuid(),
          url: link.href,
        });
        // Secure webviews cannot output events, so we need to return a promise that never resolves.
        // The webview will communicate with the parent window via a link which closes the webview.
        return new Promise(() => {});
      }

      if (!isAuthenticationEnabled) {
        return Promise.reject(new Error('Authentication is not supported'));
      }

      // short circuit if the user is already authenticated.
      if (status.status === 'authenticated') {
        return Promise.resolve(true);
      }

      try {
        const existing = checkForExistingPopup('embedded_auth');
        if (existing) {
          return existing.popupResultPromise
            .then(handleClosedPopup)
            .then((res) => Promise.resolve(res !== 'closed'));
        }
      } catch (e) {
        setStatus({
          status: 'errored',
          error: e instanceof Error ? e : Error(String(e)),
        });
        return Promise.reject(e);
      }

      setStatus({status: 'pending'});
      return createPopup({
        requestType: 'embedded_auth',
        completionPromise: (async () => {
          const authState = await frameMessenger.openPopupAndWaitForAuth(
            abortController.current.signal,
          );

          // Track how often users authenticate in embedded
          if (authState.status === 'user_authenticated') {
            analytics.track('submerchant_surfaces_user_authenticate');
          }

          return authState;
        })(),
      }).then((res) => res !== 'closed');
    };

    const onboard = async (
      linkType: OnboardingLinkType,
      hash?: string | undefined,
    ): Promise<boolean> => {
      try {
        const existing = checkForExistingPopup(linkType);
        if (existing) {
          return existing.popupResultPromise
            .then(handleClosedPopup)
            .then((res) => res !== 'closed');
        }
      } catch (e) {
        setStatus({
          status: 'errored',
          error: e instanceof Error ? e : Error(String(e)),
        });
        return Promise.reject(e);
      }

      setIsOnboarding(true);
      if (status.status !== 'authenticated') {
        setStatus({status: 'pending'});
      }

      return createPopup({
        requestType: linkType,
        hash,
        completionPromise: frameMessenger.openPopupAndWaitForComplete(
          abortController.current.signal,
        ),
      })
        .then((res) => res !== 'closed')
        .finally(() => {
          setIsOnboarding(false);
        });
    };

    const editProfile = async (action: ProfileEditType): Promise<void> => {
      try {
        const existing = checkForExistingPopup('embedded_user_update');
        if (existing) {
          return existing.popupResultPromise.then(() => Promise.resolve());
        }
      } catch (e) {
        setStatus({
          status: 'errored',
          error: e instanceof Error ? e : Error(String(e)),
        });
        return Promise.reject(e);
      }

      setIsEditingProfile(true);
      if (status.status !== 'authenticated') {
        setStatus({status: 'pending'});
      }

      return createPopup({
        requestType: 'embedded_user_update',
        searchParams: {action},
        completionPromise: frameMessenger.openPopupAndWaitForComplete(
          abortController.current.signal,
        ),
      })
        .then(() => Promise.resolve())
        .finally(() => {
          setIsEditingProfile(false);
        });
    };

    return {
      isPending: status.status === 'pending',
      isInitialized,
      isErrored: status.status === 'errored',
      error: status.status === 'errored' ? status.error : null,
      isAuthenticated: !!authenticatedUser,
      isAuthenticationRequired: isAuthenticationEnabled && !authenticatedUser,
      isOnboarding,
      isEditingProfile,
      authenticate,
      onboard,
      editProfile,
      logout,
      cancel,
      isAccountManagementSupported:
        !!authenticatedUser?.is_primary_user ||
        !!authenticatedUser?.has_onboarding_permissions,
      isApiKeyOverride: false,
    };
  }, [
    status,
    isInitialized,
    isOnboarding,
    isEditingProfile,
    frameMessenger,
    setStatus,
    createPopup,
    handleClosedPopup,
    authenticatedUser,
    isAuthenticationEnabled,
    metaOptions,
    analytics,
    reports,
    createEmbeddedLinkWrapper,
    emitEvent,
    mobileAuthenticationFlow,
    mobileSdk,
  ]);

  // This is a test-only hook
  if (submerchantAuthOverride) {
    return submerchantAuthOverride;
  }

  // If an API key override is included, authentication is a no-op and we assume the API key is already for an authenticated user
  if (apiKeyOverride || hostApp === 'loginAs') {
    const notSupportedImplementation = () => {
      throw new Error(
        'Auth method not supported when injecting API key or when used in Login as',
      );
    };
    const isAccountManagementSupported =
      hostApp === 'express' ||
      hostApp === 'standard' ||
      hostApp === 'direct_settings' ||
      isHostedOnboardingHostApp(hostApp) ||
      (hostApp === 'loginAs' && !!apiKeyOverride) ||
      hostApp === 'platform_ios' ||
      hostApp === 'platform_android';

    return {
      isPending: false,
      isInitialized: true,
      isErrored: false,
      error: null,
      // Assume user is authenticated if there's an apiKeyOverride, but false if using login as with platform auth
      isAuthenticated: !!apiKeyOverride,
      isAuthenticationRequired: false,
      isOnboarding: false,
      isEditingProfile: false,
      authenticate: notSupportedImplementation,
      onboard: notSupportedImplementation,
      editProfile: notSupportedImplementation,
      logout: notSupportedImplementation,
      cancel: notSupportedImplementation,
      isAccountManagementSupported,
      isApiKeyOverride: !!apiKeyOverride,
    };
  }

  return result;
};

type SubmerchantAuthenticationContextInit = {
  value: Omit<SubmerchantAuthenticationContextProps, 'status' | 'setStatus'>;
};

export const SubmerchantAuthenticationContextProvider: React.FC<
  SubmerchantAuthenticationContextInit
> = ({value, children}): JSX.Element => {
  const [status, setStatus] = React.useState<SubmerchantAuthenticationStatus>({
    status: 'idle',
  });

  return (
    <SubmerchantAuthenticationContext.Provider
      value={{
        ...value,
        status,
        setStatus,
      }}
    >
      {children}
    </SubmerchantAuthenticationContext.Provider>
  );
};
